import { formatPhone } from '@/hooks/use-phone-formatter'
import dayjs from '@/lib/dayjs'
import { PaymentStatus } from '@/lib/enums/PaymentStatus'
import { getPaymentMethodLabel } from '@/lib/payment-method-display'
import { normalizeCurrencyValue, parseCurrency } from '@/lib/utils'

type Money = number | string | null | undefined

interface ReceiptAddress {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
}

interface ReceiptLocation {
  address?: ReceiptAddress | null
  phone?: string | null
}

/** A payment as the order's `payment_snapshot` lists it. */
interface ReceiptPayment {
  status?: string
  type?: string
  amount?: Money
  /** What the payment paid toward this order. */
  amount_applied?: Money
  tendered_amount?: Money
  change_due?: Money
  /** Set on refund legs; a collection has none. */
  source_payment_id?: number | null
  reference?: string | null
  posted_at?: string | null
  // Read by getPaymentMethodLabel for the card brand and last four.
  stripe_payment_method?: unknown
}

interface ReceiptItem {
  id?: number
  name?: string | null
  quantity?: number | string | null
  variant_details?: { name?: string | null } | null
  variant?: { product?: { name?: string | null } | null } | null
}

/** A line of the order's document in force (`FinancialDocumentLineResource`); money is decimal strings. */
interface ReceiptDocumentLine {
  sales_order_item_id?: number | null
  /** Unit price. */
  amount?: Money
  /** Unit price times quantity, before discounts. */
  gross_total?: Money
}

/** The sales order fields a receipt prints; mirrors SalesOrderResource. */
export interface ReceiptOrder {
  id?: number | string
  reference_id?: string | null
  created_at?: string | null
  location?: ReceiptLocation | null
  organization?: {
    name?: string | null
    primary_domain?: string | null
    default_location?: ReceiptLocation | null
  } | null
  financial_state?: {
    /** The document plus the shop fee and its tax its payments were charged: the receipt's TOTAL. */
    order_total?: Money
    collected_total?: Money
    refunded_total?: Money
    balance_due_total?: Money
  } | null
  /** The lines above the TOTAL, as the API lists them (`OrderMoneySummary::lines`); amounts are decimal strings. */
  customer_total_lines?: { key: string; label: string; amount: string }[] | null
  payment_snapshot?: { payments?: ReceiptPayment[] | null } | null
  sales_order_items?: ReceiptItem[] | null
  /** The document in force (`FinancialDocumentResource`): what each item sold for. */
  document?: { line_items?: ReceiptDocumentLine[] | null } | null
}

/** One printed line, as local-print-receipt-renderer draws it. */
export interface ReceiptLine {
  text?: string
  right?: string
  rightWidth?: number
  align?: 'center'
  bold?: boolean
  size?: number
  /** Space below the line; the renderer's default line gap when left out. */
  gap?: number
  wrap?: boolean
  indent?: number
  separator?: boolean
  spacer?: boolean
  height?: number
}

type ReceiptType = 'order' | 'partial' | 'payment'

interface ReceiptFinancials {
  hasFinancialState: boolean
  collected: number
  refunded: number
  balanceDue: number
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function centered(text: string, style: Omit<ReceiptLine, 'text' | 'align'>): ReceiptLine {
  return { ...style, text, align: 'center' }
}

function money(value: Money): string {
  const amount = parseCurrency(String(value ?? 0))
  return currencyFormatter.format(Number.isFinite(amount) ? amount : 0)
}

function receiptWebsite(value: string | null | undefined): string {
  const domain = String(value || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')

  return domain ? `https://${domain}` : ''
}

function receiptFinancials(order: ReceiptOrder): ReceiptFinancials {
  const financialState = order?.financial_state

  return {
    hasFinancialState: Boolean(financialState),
    collected: normalizeCurrencyValue(financialState?.collected_total ?? 0),
    refunded: normalizeCurrencyValue(financialState?.refunded_total ?? 0),
    balanceDue: normalizeCurrencyValue(financialState?.balance_due_total ?? 0),
  }
}

function receiptType(order: ReceiptOrder): ReceiptType {
  const { collected, balanceDue } = receiptFinancials(order)
  if (collected <= 0) return 'order'
  if (balanceDue > 0) return 'partial'
  return 'payment'
}

function receiptTypeLabel(order: ReceiptOrder): string {
  return {
    order: 'ORDER SUMMARY',
    partial: 'PARTIAL PAYMENT RECEIPT',
    payment: 'PAYMENT RECEIPT',
  }[receiptType(order)]
}

export function receiptDocumentLabel(order: ReceiptOrder): string {
  return {
    order: 'Order summary',
    partial: 'Partial payment receipt',
    payment: 'Payment receipt',
  }[receiptType(order)]
}

function shopAddressLines(order: ReceiptOrder): string[] {
  const address = order?.location?.address ?? order?.organization?.default_location?.address
  if (!address) return []

  const cityLine = [address.city, address.state, address.postal_code].filter(Boolean).join(' ')
  return [address.line1, address.line2, cityLine].filter((line): line is string => Boolean(line))
}

function orderTimestampLines(order: ReceiptOrder): ReceiptLine[] {
  if (!order?.created_at) return []

  const timestamp = dayjs(order.created_at)
  if (!timestamp.isValid()) return []

  return [
    { text: 'Date', right: timestamp.format('MMM D, YYYY'), rightWidth: 190 },
    { text: 'Time', right: timestamp.format('h:mm A'), rightWidth: 190 },
  ]
}

function appliedAmount(payment: ReceiptPayment): number {
  return normalizeCurrencyValue(payment.amount_applied ?? 0)
}

/** The paid collections toward the order: the receipt's payment lines and what opens the cash drawer. */
export function paidReceiptPayments(order: ReceiptOrder): ReceiptPayment[] {
  return (order?.payment_snapshot?.payments ?? []).filter(
    (payment) =>
      payment.status === PaymentStatus.Paid.Value &&
      payment.source_payment_id == null &&
      appliedAmount(payment) > 0
  )
}

function paymentBelongsOnlyToThisOrder(payment: ReceiptPayment, amount: number): boolean {
  if (payment.amount == null) return false

  return Math.abs(normalizeCurrencyValue(payment.amount) - amount) < 0.005
}

function paymentLines(order: ReceiptOrder, financials: ReceiptFinancials): ReceiptLine[] {
  const payments = paidReceiptPayments(order)
  const kind = receiptType(order)

  if (!payments.length && kind === 'order' && financials.balanceDue > 0) {
    return [
      { separator: true },
      { text: 'PAYMENT', bold: true, size: 16 },
      { text: 'Payment method', right: 'Pay later', rightWidth: 170 },
      { text: 'Balance due', right: money(financials.balanceDue), bold: true, size: 22 },
    ]
  }

  if (!payments.length && (!financials.hasFinancialState || financials.collected <= 0)) {
    return []
  }

  const lines: ReceiptLine[] = [
    { separator: true },
    { text: payments.length === 1 ? 'PAYMENT' : 'PAYMENTS', bold: true, size: 16 },
  ]

  payments.forEach((payment) => {
    const amount = appliedAmount(payment)
    lines.push({
      // payment-method-display is untyped JavaScript.
      text: String(getPaymentMethodLabel(payment)),
      right: money(amount),
      rightWidth: 150,
      bold: true,
      wrap: true,
    })

    if (
      payment.type === 'cash' &&
      payment.tendered_amount != null &&
      paymentBelongsOnlyToThisOrder(payment, amount)
    ) {
      const tenderedAmount = normalizeCurrencyValue(payment.tendered_amount)
      const changeDue = normalizeCurrencyValue(payment.change_due ?? 0)
      lines.push(
        { text: 'Cash tendered', right: money(tenderedAmount), rightWidth: 150, indent: 16 },
        { text: 'Change', right: money(changeDue), rightWidth: 150, indent: 16 }
      )
    }

    if (payment.reference && payment.type !== 'check') {
      lines.push({ text: `Reference ${payment.reference}`, indent: 16, size: 14, wrap: true })
    }

    if (payment.posted_at) {
      const paidAt = dayjs(payment.posted_at)
      if (paidAt.isValid()) {
        lines.push({
          text: paidAt.format('MMM D, YYYY [at] h:mm A'),
          indent: 16,
          size: 14,
          wrap: true,
        })
      }
    }
  })

  if (financials.hasFinancialState) {
    lines.push({
      text: 'Paid total',
      right: money(financials.collected),
      rightWidth: 150,
      bold: payments.length > 1,
    })
    if (financials.refunded > 0) {
      lines.push({ text: 'Refunded', right: money(financials.refunded), rightWidth: 150 })
    }
  }

  return lines
}

export function buildReceiptLines(
  order: ReceiptOrder,
  { reprint = false, hasLogo = false }: { reprint?: boolean; hasLogo?: boolean } = {}
): ReceiptLine[] {
  const items = order?.sales_order_items ?? []
  const location = order?.location ?? order?.organization?.default_location
  const phone = location?.phone
  const website = receiptWebsite(order?.organization?.primary_domain)
  const financials = receiptFinancials(order)
  const lines: ReceiptLine[] = [
    ...(!hasLogo
      ? [
          centered(order?.organization?.name || 'OneStem Flower Shop', {
            bold: true,
            size: 28,
            wrap: true,
          }),
        ]
      : []),
    ...shopAddressLines(order).map((text) => centered(text, { size: 16, wrap: true })),
    ...(phone ? [centered(formatPhone(phone), { size: 16 })] : []),
    ...(website ? [centered(website, { size: 16, wrap: true })] : []),
    { spacer: true, height: 4 },
    centered(`${receiptTypeLabel(order)}${reprint ? ' - REPRINT' : ''}`, {
      bold: true,
      size: 22,
      wrap: true,
    }),
    centered(`Order ${order?.reference_id || order?.id || ''}`, { size: 18 }),
    { separator: true },
    ...orderTimestampLines(order),
    ...(order?.created_at ? [{ separator: true }] : []),
    { text: 'ITEM', right: 'TOTAL', rightWidth: 130, bold: true, size: 16 },
  ]

  const documentLines = new Map(
    (order?.document?.line_items ?? []).map((line) => [line.sales_order_item_id, line])
  )
  items.forEach((item) => {
    const name = item?.variant_details?.name || item?.variant?.product?.name || item?.name || 'Item'
    const quantity = Number(item?.quantity || 1)
    const line = documentLines.get(item?.id)
    lines.push({
      text: `${quantity} × ${name}`,
      right: money(line?.gross_total),
      rightWidth: 150,
      wrap: true,
    })
    if (quantity > 1) {
      lines.push({ text: `${money(line?.amount)} each`, indent: 16, size: 14 })
    }
  })

  lines.push(
    { separator: true },
    // The lines above the TOTAL and the TOTAL, as the API lists them for every receipt and print.
    ...(order?.customer_total_lines ?? []).map((line) => ({
      text: line.label,
      right: money(line.amount),
      rightWidth: 150,
    })),
    { separator: true },
    {
      text: 'TOTAL',
      right: money(order?.financial_state?.order_total),
      rightWidth: 170,
      bold: true,
      size: 24,
    },
    ...paymentLines(order, financials),
    { separator: true },
    centered('Thank you!', { bold: true, size: 22 }),
    centered('We appreciate your business.', { size: 16, wrap: true })
  )

  return lines
}
